import { useCallback, useEffect, useMemo, useState } from 'react';
import '../api-status.css';
import {
  fetchJsonWithWakeRetry,
  type ApiConnectionPhase,
} from '../../../sites/shared/browserApi';

type PlatformApiPhase = ApiConnectionPhase | 'not-configured';

type ReadyResponse = {
  status: string;
  database: string;
};

export type PlatformApiReadiness = {
  phase: PlatformApiPhase;
  title: string;
  detail: string;
  badge: string;
  tone: 'healthy' | 'pending' | 'unavailable';
};

const presentation: Record<PlatformApiPhase, Omit<PlatformApiReadiness, 'phase'>> = {
  'not-configured': {
    title: 'API connection not configured',
    detail: 'The Admin API origin must be configured for this environment.',
    badge: 'CONFIG',
    tone: 'unavailable',
  },
  connecting: {
    title: 'Getting things ready…',
    detail: 'Connecting securely to the platform API.',
    badge: 'WAIT',
    tone: 'pending',
  },
  waking: {
    title: 'System is waking up…',
    detail: 'Staging may need a little longer after being idle. Retrying automatically.',
    badge: 'WAKE',
    tone: 'pending',
  },
  ready: {
    title: 'Platform API ready',
    detail: 'Application and database readiness checks passed.',
    badge: 'READY',
    tone: 'healthy',
  },
  unavailable: {
    title: 'Platform API is not ready yet',
    detail: 'The connection could not be established. You can retry without reloading the page.',
    badge: 'RETRY',
    tone: 'unavailable',
  },
};

export function usePlatformApiReadiness() {
  const apiBaseUrl = useMemo(
    () => (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/$/, ''),
    [],
  );
  const [phase, setPhase] = useState<PlatformApiPhase>(
    apiBaseUrl ? 'connecting' : 'not-configured',
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!apiBaseUrl) {
      setPhase('not-configured');
      return undefined;
    }

    const controller = new AbortController();

    void fetchJsonWithWakeRetry<ReadyResponse>(`${apiBaseUrl}/health/ready`, {
      signal: controller.signal,
      wakeAfterMs: 7_000,
      maxWaitMs: 75_000,
      retryDelayMs: 4_000,
      requestTimeoutMs: 12_000,
      onPhaseChange: setPhase,
    });

    return () => controller.abort();
  }, [apiBaseUrl, attempt]);

  const retry = useCallback(() => {
    if (!apiBaseUrl) return;
    setPhase('connecting');
    setAttempt((current) => current + 1);
  }, [apiBaseUrl]);

  return {
    status: {
      phase,
      ...presentation[phase],
    } satisfies PlatformApiReadiness,
    retry,
  };
}
